'use client'

import { useEffect } from 'react'
import { useAuth } from '@clerk/nextjs'
import { useQueryClient } from '@tanstack/react-query'
import { io } from 'socket.io-client'
import { env } from '@/config/env'

/** Same event name the backend's `NotificationsGateway` emits — see
 * `apps/nivex-backend/.../notification-broadcaster.ts`. */
const NOTIFICATION_EVENT = 'notification'

/**
 * Live push for the notification bell — a thin layer on top of the existing REST polling, not a
 * replacement for it: `useNotifications()` still does the actual initial fetch. This just
 * invalidates that query's cache the moment a `notification` event arrives, so the bell updates
 * without waiting for its next natural refetch.
 *
 * Connects to `NEXT_PUBLIC_API_URL` directly (the bare origin, not `/api`) — the backend's
 * `NotificationsGateway` shares the same HTTP server/port as the REST API, but socket.io's
 * transport is independent of Express's `/api` prefix.
 *
 * `auth` is a **callback**, not a static token captured once — socket.io invokes it fresh on the
 * initial connect and on every automatic reconnection attempt, mirroring the same per-call
 * freshness `useApiFetch` already relies on for every REST request (Clerk's `getToken()` handles
 * its own refresh under the hood). No manual periodic reconnect is needed on top of that.
 */
export function useNotificationSocket(): void {
  const { getToken, isSignedIn } = useAuth()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isSignedIn) return

    const socket = io(env.NEXT_PUBLIC_API_URL, {
      auth: (callback) => {
        getToken()
          .then((token) => callback({ token }))
          .catch(() => callback({}))
      },
    })

    socket.on(NOTIFICATION_EVENT, () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    })

    return () => {
      socket.disconnect()
    }
  }, [isSignedIn, getToken, queryClient])
}
